import {
  createInventorizationBlockStatuses,
  createInventorizationIssue,
  INVENTORIZATION_BLOCK_IDS,
  type InventorizationBlockId,
  inventorizationBlocks,
} from '@/features/inventorization/domain/inventorization';
import { INVENTORIZE_REVIEW_STATUSES } from '@/features/inventorization/domain/review/inventorization-review-status';
import { INVENTORIZE_REVIEW_FIELD_ID_PREFIXES } from '@/features/inventorization/domain/review/inventorize-review-field-ids';
import { createInventorizeReviewFields } from '@/features/inventorization/domain/review/inventorize-review-fields';
import type { CaptureSession, ValidationIssue } from '@/shared/capture/capture-session';
import { BLOCK_STATUSES } from '@/shared/capture/capture-session';
import { translateInventoryText } from '@/shared/i18n/inventory-i18n';

interface InventorizationReadinessOptions {
  includeReviewBlock?: boolean;
}

export function validateInventorizationReadiness(
  session: CaptureSession,
  options: InventorizationReadinessOptions = {},
): ValidationIssue[] {
  const includeReviewBlock = options.includeReviewBlock ?? true;
  const blockStatuses = {
    ...createInventorizationBlockStatuses(),
    ...session.blockStatuses,
  };
  const reviewFields = createInventorizeReviewFields(session);
  const blockIssues = inventorizationBlocks.flatMap<ValidationIssue>(block => {
    if (block.id === INVENTORIZATION_BLOCK_IDS.review && !includeReviewBlock) {
      return [];
    }

    const status = blockStatuses[block.id];

    if (status === BLOCK_STATUSES.valid || status === BLOCK_STATUSES.warning) {
      return [];
    }

    if (isEffectivelyCompleteBlock(block.id, reviewFields)) {
      return [];
    }

    return [
      createInventorizationIssue(
        block.id,
        `BLOCK_${block.id.toUpperCase()}_${status.toUpperCase()}`,
        readBlockIncompleteMessage(block.id),
      ),
    ];
  });
  const photoIssues = reviewFields.flatMap<ValidationIssue>(field => {
    if (
      !(
        field.id.startsWith(INVENTORIZE_REVIEW_FIELD_ID_PREFIXES.photos) &&
        field.required &&
        field.status === INVENTORIZE_REVIEW_STATUSES.empty
      )
    ) {
      return [];
    }

    return [
      createInventorizationIssue(
        field.targetBlockId,
        `PHOTO_${field.id
          .replace(INVENTORIZE_REVIEW_FIELD_ID_PREFIXES.photos, '')
          .toUpperCase()
          .replace(/[^A-Z0-9]+/g, '_')}_MISSING`,
        translateInventoryText('app.inventory.validation.photoRequired', 'Required photo is missing: {photo}.', {
          photo: field.label,
        }),
      ),
    ];
  });

  return [...blockIssues, ...photoIssues];
}

// Whole sentences per block: Czech and Polish agree the verb with the block name's gender and number.
function readBlockIncompleteMessage(blockId: InventorizationBlockId) {
  switch (blockId) {
    case INVENTORIZATION_BLOCK_IDS.context:
      return translateInventoryText(
        'app.inventory.validation.block.contextIncomplete',
        'Location details are not complete.',
      );
    case INVENTORIZATION_BLOCK_IDS.identity:
      return translateInventoryText('app.inventory.validation.block.identityIncomplete', 'Identity is not complete.');
    case INVENTORIZATION_BLOCK_IDS.readings:
      return translateInventoryText('app.inventory.validation.block.readingsIncomplete', 'Registers are not complete.');
    case INVENTORIZATION_BLOCK_IDS.position:
      return translateInventoryText('app.inventory.validation.block.positionIncomplete', 'Position is not complete.');
    case INVENTORIZATION_BLOCK_IDS.technical:
      return translateInventoryText(
        'app.inventory.validation.block.technicalIncomplete',
        'Remote reading details are not complete.',
      );
    case INVENTORIZATION_BLOCK_IDS.review:
      return translateInventoryText('app.inventory.validation.block.reviewIncomplete', 'Review is not complete.');
  }
}

function isEffectivelyCompleteBlock(
  blockId: InventorizationBlockId,
  fields: ReturnType<typeof createInventorizeReviewFields>,
) {
  if (blockId === INVENTORIZATION_BLOCK_IDS.position || blockId === INVENTORIZATION_BLOCK_IDS.review) {
    return false;
  }

  const requiredFields = fields.filter(field => field.required && field.targetBlockId === blockId);

  return requiredFields.length > 0 && requiredFields.every(isEffectiveField);
}

function isEffectiveField(field: ReturnType<typeof createInventorizeReviewFields>[number]) {
  return (
    Boolean(field.value) &&
    (field.status === INVENTORIZE_REVIEW_STATUSES.manual ||
      field.status === INVENTORIZE_REVIEW_STATUSES.parsed ||
      field.status === INVENTORIZE_REVIEW_STATUSES.accepted)
  );
}

export function validateInventorizationReviewPrerequisites(session: CaptureSession) {
  return validateInventorizationReadiness(session, { includeReviewBlock: false });
}
